import { canOperateIntegrations } from '../pages/integrations/integration-contract';
import { Layout as RALayout, Menu, AppBar, usePermissions } from 'react-admin';
import type { LayoutProps } from 'react-admin';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import VerifiedIcon from '@mui/icons-material/Verified';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import GridOnIcon from '@mui/icons-material/GridOn';
import CategoryIcon from '@mui/icons-material/Category';
import TimelineIcon from '@mui/icons-material/Timeline';
import BalanceIcon from '@mui/icons-material/Balance';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import GroupsIcon from '@mui/icons-material/Groups';
import { Divider, Typography, Box } from '@mui/material';
import { OnboardingBanner } from './OnboardingBanner';
import { SessionExpiryWatcher } from './SessionExpiryWatcher';
import { ProvisioningBanner } from './ProvisioningBanner';
import { ForbiddenWatcher } from './ForbiddenWatcher';
import type { Permissions } from '../types';
import { can } from '../utils/capability-helpers';
import { canonicalSurveyOrgPath } from '../pages/capture/cadre-survey-intake-contract';

const SectionLabel = ({ step, label, hint }: { step?: number; label: string; hint?: string }) => (
  <Box px={2} pt={1.5} pb={0.25}>
    <Box display="flex" alignItems="baseline" gap={0.5}>
      {step !== undefined && (
        <Typography sx={{ color: 'primary.main', fontWeight: 800, fontSize: '0.6rem', opacity: 0.55, lineHeight: 1 }}>
          {step}
        </Typography>
      )}
      <Typography
        variant="overline"
        sx={{ color: 'text.primary', fontWeight: 700, fontSize: '0.72rem', lineHeight: 1 }}
      >
        {label}
      </Typography>
    </Box>
    {hint && (
      <Typography
        variant="caption"
        sx={{ color: 'text.disabled', fontSize: '0.63rem', display: 'block', lineHeight: 1.3, mt: 0.25 }}
      >
        {hint}
      </Typography>
    )}
  </Box>
);

export const CustomMenu = () => {
  const { permissions } = usePermissions<Permissions>();

  const showBuild = can(permissions, 'manage_org') ||
    can(permissions, 'manage_scoring_tables') ||
    can(permissions, 'view_events') ||
    can(permissions, 'manage_assessments');

  const showStaff = can(permissions, 'manage_users') || can(permissions, 'manage_teams');

  const showAssess = can(permissions, 'manage_assessments') || can(permissions, 'schedule_instances');

  const showRecord = can(permissions, 'record_attempts') || can(permissions, 'manage_assessments');

  const showReview = can(permissions, 'view_analytics') || can(permissions, 'manage_org');

  const showLicensing = can(permissions, 'manage_licensing');
  const showIntegrations = can(permissions, 'manage_integrations');

  return (
    <Menu>
      {canOperateIntegrations(permissions) && <Menu.Item to="/integration-reconciliation" primaryText="Simulated Integration Reconciliation" leftIcon={<SwapHorizIcon />} />}
      <Menu.Item to="/measurement-releases" primaryText="Measurement Releases" leftIcon={<VerifiedIcon />} />
      <Menu.Item to="/fitness-assessments" primaryText="Declared Data Checks" leftIcon={<VerifiedIcon />} />
      <Menu.Item to="/existing-participation-record" primaryText="Existing Participation Record" leftIcon={<VerifiedIcon />} />
      <Menu.Item to="/participation-limitations" primaryText="Governed Participation Limitations" leftIcon={<VerifiedIcon />} />
      <Menu.Item to="/event-substitutions" primaryText="Alternative Event Preparation" leftIcon={<VerifiedIcon />} />
      <Menu.Item to="/lane-preparation" primaryText="Lane Preparation" leftIcon={<VerifiedIcon />} />
      <Menu.Item to="/human-review" primaryText="Human Evidence Review" leftIcon={<VerifiedIcon />} />
      <Menu.Item to="/outcome-links" primaryText="Outcome Evidence Links" leftIcon={<VerifiedIcon />} />
      <Menu.Item to="/learner-evidence" primaryText="Learner Evidence" leftIcon={<VerifiedIcon />} />
      <Menu.Item to="/team-capture" primaryText="Configured Team Capture" leftIcon={<VerifiedIcon />} />
      <Menu.Item to="/capture-governance" primaryText="Capture Quality and Corrections" leftIcon={<VerifiedIcon />} />
      <Menu.Item to="/configuration-executions" primaryText="Configuration Preview and Execution" leftIcon={<CategoryIcon />} />
      <Menu.Item to="/configurations" primaryText="Versioned Configuration" leftIcon={<CategoryIcon />} />
      {showBuild && (
        <>
          <SectionLabel step={1} label="BUILD" hint="Org structure, scoring & events" />
          {can(permissions, 'manage_org') && (
            <Menu.ResourceItem name="org_units" primaryText="Org Units" />
          )}
          {can(permissions, 'view_events') && (
            <Menu.ResourceItem name="events" primaryText="Events" />
          )}
          {can(permissions, 'manage_scoring_tables') && (
            <Menu.ResourceItem name="score_tables" primaryText="Scoring Tables" />
          )}
          {can(permissions, 'manage_assessments') && (
            <Menu.ResourceItem name="courses" primaryText="Courses" />
          )}
          {can(permissions, 'manage_assessments') && (
            <Menu.ResourceItem name="mos_profiles" primaryText="MOS / AOC Profiles" />
          )}
          <Divider sx={{ my: 1 }} />
        </>
      )}

      {showStaff && (
        <>
          <SectionLabel step={2} label="STAFF" hint="People, teams & classes" />
          {can(permissions, 'manage_users') && (
            <Menu.ResourceItem name="users" primaryText="Staff" />
          )}
          {can(permissions, 'manage_users') && (
            <Menu.ResourceItem name="students" primaryText="Students" />
          )}
          {can(permissions, 'manage_users') && (
            <Menu.ResourceItem name="student_groups" primaryText="Classes" />
          )}
          {can(permissions, 'manage_teams') && (
            <Menu.ResourceItem name="teams" primaryText="Teams" />
          )}
          <Divider sx={{ my: 1 }} />
        </>
      )}

      {showAssess && (
        <>
          <SectionLabel step={3} label="ASSESS" hint="Design & schedule assessments" />
          {can(permissions, 'manage_assessments') && (
            <Menu.ResourceItem name="assessments" primaryText="Assessments" />
          )}
          {can(permissions, 'schedule_instances') && (
            <Menu.ResourceItem name="instances" primaryText="Assessment Schedule" />
          )}
          <Divider sx={{ my: 1 }} />
        </>
      )}

      {showRecord && (
        <>
          <SectionLabel step={4} label="RECORD" hint="Log attempts & exceptions" />
          {can(permissions, 'record_attempts') && (
            <Menu.Item
              to="/batch-grade"
              primaryText="Batch Grade"
              leftIcon={<GridOnIcon />}
            />
          )}
          {can(permissions, 'record_attempts') && permissions?.assignments.some(assignment => assignment.role === 'instructor' && canonicalSurveyOrgPath(assignment.orgPath)) && (
            <Menu.Item
              to="/cadre-survey-entry"
              primaryText="Cadre Survey Entry"
              leftIcon={<GridOnIcon />}
            />
          )}
          {can(permissions, 'record_attempts') && (
            <Menu.ResourceItem name="attempts" primaryText="Event Attempts" />
          )}
          {can(permissions, 'manage_assessments') && (
            <Menu.ResourceItem name="disqualifications" primaryText="Participation Records" />
          )}
          <Divider sx={{ my: 1 }} />
        </>
      )}

      {showReview && (
        <>
          <SectionLabel step={5} label="REVIEW" hint="Performance & trends" />
          {can(permissions, 'view_analytics') && (
            <Menu.DashboardItem primaryText="Dashboard" leftIcon={<DashboardIcon />} />
          )}
          {can(permissions, 'view_analytics') && (
            <Menu.Item
              to="/analytics"
              primaryText="Analytics"
              leftIcon={<BarChartIcon />}
            />
          )}
          {can(permissions, 'view_analytics') && (
            <Menu.Item
              to="/cohort-ksb"
              primaryText="Cohort KSB"
              leftIcon={<CategoryIcon />}
            />
          )}
          {can(permissions, 'view_analytics') && (
            <Menu.Item
              to="/cohort-outcomes"
              primaryText="Cohort Outcomes (+N mo)"
              leftIcon={<TimelineIcon />}
            />
          )}
          {can(permissions, 'view_analytics') && (
            <Menu.Item
              to="/inter-rater"
              primaryText="Inter-rater calibration"
              leftIcon={<BalanceIcon />}
            />
          )}
          {can(permissions, 'view_analytics') && (
            <Menu.Item
              to="/calibration-consensus"
              primaryText="Calibration consensus"
              leftIcon={<GroupsIcon />}
            />
          )}
          {can(permissions, 'view_analytics') && (
            <Menu.Item
              to="/phase-board"
              primaryText="Phase boards"
              leftIcon={<ViewModuleIcon />}
            />
          )}
          {can(permissions, 'manage_org') && (
            <Menu.ResourceItem name="rankings" primaryText="Rankings" />
          )}
          <Divider sx={{ my: 1 }} />
        </>
      )}

      {showLicensing && (
        <>
          <SectionLabel label="Licensing" />
          <Menu.Item
            to="/licensing"
            primaryText="Licensing"
            leftIcon={<VerifiedIcon />}
          />
          <Menu.ResourceItem name="licenses" primaryText="Licenses" />
          <Divider sx={{ my: 1 }} />
        </>
      )}

      {showIntegrations && (
        <>
          <SectionLabel label="Integration" />
          <Menu.Item
            to="/data-exchange"
            primaryText="Data Exchange"
            leftIcon={<SwapHorizIcon />}
          />
        </>
      )}
    </Menu>
  );
};

const CustomAppBar = () => (
  <AppBar>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
      <img src="/aetc-logo.jpeg" alt="AETC" style={{ height: 36 }} />
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        AETC Admin
      </Typography>
    </Box>
  </AppBar>
);

const CustomLayout = (props: LayoutProps) => (
  <RALayout
    {...props}
    menu={CustomMenu}
    appBar={CustomAppBar}
    sx={{ '& .RaLayout-content': { pt: 0 } }}
  >
    <SessionExpiryWatcher />
    <ForbiddenWatcher />
    <ProvisioningBanner />
    <OnboardingBanner />
    {props.children}
  </RALayout>
);

export default CustomLayout;
